import type { Dict, Mixpanel } from "mixpanel-browser";
import { ENVIRONMENTS } from "../../constants";
import { getEnvironment } from "../../utils";

const MIXPANEL_EU_API_HOST_URL = "https://api-eu.mixpanel.com";
const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
const MIXPANEL_PROPERTY_BLACKLIST = [
    "$current_url",
    "$initial_referrer",
    "$initial_referring_domain",
    "$referrer",
    "$referring_domain",
    "$search_engine",
    "mp_keyword",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
];

const isProduction = getEnvironment() === ENVIRONMENTS.PRODUCTION;

let mixpanelInstancePromise: Promise<Mixpanel | null> | null = null;

const initMixpanel = async (token: string): Promise<Mixpanel | null> => {
    try {
        const { default: mixpanel } = await import("mixpanel-browser");

        mixpanel.init(token, {
            api_host: MIXPANEL_EU_API_HOST_URL,
            disable_persistence: true,
            stop_utm_persistence: true,
            track_pageview: false,
            autocapture: false,
            batch_requests: false,
            ip: false,
            property_blacklist: MIXPANEL_PROPERTY_BLACKLIST,
            debug: !isProduction,
            ignore_dnt: !isProduction,
        });
        mixpanel.register({ app_environment: getEnvironment() });

        return mixpanel;
    } catch (error) {
        console.warn(
            "Mixpanel could not be loaded, tracking is disabled",
            error
        );

        return null;
    }
};

const getMixpanel = (): Promise<Mixpanel | null> => {
    if (typeof window === "undefined") {
        return Promise.resolve(null);
    }

    if (!mixpanelInstancePromise) {
        if (MIXPANEL_TOKEN) {
            mixpanelInstancePromise = initMixpanel(MIXPANEL_TOKEN);
        } else {
            console.warn(
                "NEXT_PUBLIC_MIXPANEL_TOKEN is not set, tracking is disabled"
            );
            mixpanelInstancePromise = Promise.resolve(null);
        }
    }

    return mixpanelInstancePromise;
};

const withMixpanel = (run: (mixpanel: Mixpanel) => void) => {
    void getMixpanel().then((mixpanel) => {
        if (mixpanel) {
            run(mixpanel);
        }
    });
};

export const trackMixpanelEvent = (eventName: string, properties?: Dict) =>
    withMixpanel((mixpanel) => mixpanel.track(eventName, properties));

export const startMixpanelEventTimer = (eventName: string) =>
    withMixpanel((mixpanel) => mixpanel.time_event(eventName));

export const setMixpanelEventContext = (properties: Dict) =>
    withMixpanel((mixpanel) => mixpanel.register(properties));
